import {t} from 'i18next';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {Box, Grid, InputAdornment, Typography} from '@mui/material';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {useEffect, useMemo, useState} from 'react';
import {Search} from '@mui/icons-material';
import './NewFolder.scss';
import ArrMultiSelectDropdown from '../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {useGetBulkTagList} from '../../../../hooks/assignments/useAssignments';
import {transformJsTreeApiToTreeData} from '../../actionMenuUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {PROPOGATION_TYPE} from '../../../../shared/constants';
import Clear from '@mui/icons-material/CancelOutlined';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';

const options: ISelectItem[] = [
  {id: PROPOGATION_TYPE.FAMILY, name: 'Family Members'},
  {id: PROPOGATION_TYPE.DUPLICATES, name: 'Exact Duplicates (Uses MD5Hash)'},
  {id: PROPOGATION_TYPE.EMAILTHREADS, name: 'Email Threads'},
  {id: PROPOGATION_TYPE.EMAILDUPLICATES, name: 'Email Duplicates'},
];

interface TreeItem {
  id: string;
  label: string;
  fileType?: string;
  children?: TreeItem[];
}

interface INewFolderProps {
  selectedNewFolder: string;
  setSelectedNewFolder: (value: string | null | undefined) => void;
  searchInputNewFolders: string;
  setSearchInputNewFolders: (value: string) => void;
  name: string;
  handleChangeName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isNameInputValid: boolean;
  handleBlurName: (fieldName: 'name') => void;
  setErrorMsg: () => string;
  propagateFolder: ISelectItem[];
  setPropagateFolder: (value: ISelectItem[]) => void;
}

const removeLeafNodes = (nodes: TreeItem[] | undefined): TreeItem[] => {
  if (!nodes) return [];

  return nodes
    .filter((node) => node.children && node.children.length > 0)
    .map((node) => ({
      ...node,
      children: removeLeafNodes(node.children),
    }));
};

// Helper function to get expanded ids based on the search keyword
const getExpandedIdsForSearch = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  keyword: string
): string[] => {
  const ids = new Set<string>();
  const text = keyword.toLowerCase();

  const dfs = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    const isMatch = node.label?.toLowerCase().includes(text);
    let childMatch = false;

    if (node.children?.length) {
      for (const child of node.children) {
        if (dfs(child)) childMatch = true;
      }
    }

    if (isMatch || childMatch) {
      ids.add(node.id);
      return true;
    }

    return false;
  };

  nodes.forEach((root) => dfs(root));
  return Array.from(ids);
};

// Expand all nodes on initial load
const getAllIds = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
): string[] => {
  const result: string[] = [];
  const dfs = (n: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    result.push(n.id);
    n.children?.forEach(dfs);
  };
  nodes.forEach(dfs);
  return result;
};

const NewFolder: React.FC<INewFolderProps> = ({
  selectedNewFolder,
  setSelectedNewFolder,
  searchInputNewFolders,
  setSearchInputNewFolders,
  name,
  handleChangeName,
  isNameInputValid,
  handleBlurName,
  setErrorMsg,
  propagateFolder,
  setPropagateFolder,
}) => {
  const enBulkFolder = 'actionMenu.bulkFolderFlyout';
  const [searchInput, setSearchInput] = useState<string>(searchInputNewFolders);
  const [treeKey, setTreeKey] = useState<number>(0);
  const [defaultExpandedIds, setDefaultExpandedIds] = useState<string[]>([]);

  const {data: BulkFolderData, isPending: isLoading} =
    useGetBulkTagList('BulkFolder');
  const treeData = useMemo(() => {
    return transformJsTreeApiToTreeData(BulkFolderData);
  }, [BulkFolderData]);

  useEffect(() => {
    if (!treeData || treeData.length === 0) return;

    const allIds = getAllIds(treeData);
    setDefaultExpandedIds(allIds);
    setTreeKey((prev) => prev + 1); // remount to apply expandedIds
  }, [treeData]);

  useEffect(() => {
    const expanded = getExpandedIdsForSearch(treeData, searchInputNewFolders);
    setDefaultExpandedIds(expanded);
    setTreeKey((prev) => prev + 1); // force remount to apply expandedIds
  }, [searchInputNewFolders, treeData]);

  const cleanedTreeData = removeLeafNodes(treeData);
  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setSearchInputNewFolders(searchInput.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchInputNewFolders('');
    setSearchInput('');
    setDefaultExpandedIds(getAllIds(treeData));
    setTreeKey((prev) => prev + 1); // remount tree to apply
  };

  const handleSelected = (values: string[] | null) => {
    setSelectedNewFolder(values?.[0]);
  };
  const handlePropagateChange = (selected: ISelectItem[]) => {
    setPropagateFolder(selected);
  };

  return (
    <div className="bulk-new-folder-tab-container" data-testid="new-folder-div">
      <Box className="search-box">
        <Grid container columnSpacing={2} rowSpacing={1.5}>
          <Grid size={{xs: 12, sm: 6}}>
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              className="search-input"
              placeholder={t(`${enBulkFolder}.searchPlaceholder`)}
              aria-label={t(`${enBulkFolder}.searchPlaceholder`)}
              dataTestId="new-folder-search-input"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
              }}
              onKeyDown={handleSearchKeyEnter}
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="end"
                      className="search-adornment-icon"
                    >
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment
                      position="end"
                      className="clear-adornment-icon"
                    >
                      <Clear
                        onClick={handleClearSearch}
                        style={{cursor: 'pointer'}}
                        data-testid="search-clear-btn"
                      />
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Grid>
          <Grid size={{xs: 12, sm: 6}}></Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <Box className="custom-text-field">
              <Typography
                className="typo-text body3_size input-label"
                data-testid="input-label-name"
                tabIndex={0}
              >
                {t(`${enBulkFolder}.requiredName`)}
              </Typography>
              <ArrTextField
                id="name-input"
                state={isNameInputValid ? 'error' : 'enabled'}
                dataTestId="new-folder-name-input"
                placeholder={t(`${enBulkFolder}.namePlaceholder`)}
                sizeVariant="medium"
                required
                size="small"
                value={name}
                aria-required
                onChange={handleChangeName}
                onBlur={() => handleBlurName('name')}
                supportText={setErrorMsg()}
                showErrorIcon={isNameInputValid}
              />
            </Box>
          </Grid>
          <Grid size={{xs: 12, sm: 6}}>
            <Box className="custom-text-field">
              <Typography
                className="typo-text body3_size input-label"
                data-testid="input-label-propagate-folder"
                tabIndex={0}
              >
                {t(`${enBulkFolder}.propagateFolder`)}&nbsp;
                <ArrTooltipWrapper
                  trigger={
                    <InfoIcon
                      className="info-icon-label"
                      data-testid="toggle-info-tooltip"
                    />
                  }
                  title={t(`${enBulkFolder}.propagateFolder`)}
                >
                  {t(`${enBulkFolder}.propagateFolderInfo`)}
                </ArrTooltipWrapper>
                :
              </Typography>
              <ArrMultiSelectDropdown
                dropdownLabel={t('common.select')}
                dropdownItems={options}
                selectedValues={propagateFolder}
                onSelectionChange={handlePropagateChange}
                multiple
                searchable={false}
                withControls
                sizeVariant="medium"
                checkboxDataTestId="new-folder-propagate-folder-dropdown-checkbox"
                onClearAll={() => {}}
                onApply={() => {}}
                isShowTooltip={false}
                dataTestId="msd-new-folder-select"
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box className="tree-heading-box">
        <Typography
          className="typo-text subhead4_size"
          data-testid="select-parent-for-newFolder"
          tabIndex={0}
        >
          {t(`${enBulkFolder}.selectParentForNewFolder`)}
        </Typography>
      </Box>
      <Box className="folder-tree-box  new-tree" data-testid="new-folder-tree">
        {isLoading ||
        (!searchInputNewFolders &&
          (!treeData.length || !defaultExpandedIds.length)) ? (
          <ArrLoadingSpinner />
        ) : (
          <ArrTreeView
            key={treeKey}
            itemsList={cleanedTreeData}
            selectedItem={selectedNewFolder}
            handleNoResultMessage={t(`${enBulkFolder}.noResult`)}
            allowGlobalSelect={true}
            treeSelectedItems={[]}
            multipleSelection={false}
            isShowCountLabel={false}
            isShowCheckbox={false}
            showActions={false}
            customContentHeight={36}
            setSelectedItem={handleSelected}
            searchKeyword={searchInputNewFolders}
            defaultExpandedIds={defaultExpandedIds}
            setExpandedFolders={setDefaultExpandedIds}
          />
        )}
      </Box>
    </div>
  );
};

export default NewFolder;
